import type { SeasonTime } from '../services/seasonTime.service';

export type SeasonStatus = 'upcoming' | 'in-season' | 'out-of-season';

export const UPCOMING_THRESHOLD_DAYS = 30;

/**
 * Determines whether a season is currently Upcoming (within thresholdDays before
 * start), In Season (between start and end), or Out of Season.
 *
 * Handles seasons that wrap across the calendar year (e.g. November → February).
 */
export function getSeasonStatus(season: SeasonTime, today: Date, thresholdDays: number = UPCOMING_THRESHOLD_DAYS): SeasonStatus {
  const y = today.getFullYear();
  const start = new Date(y, season.startMonth - 1, season.startDay);
  const end   = new Date(y, season.endMonth   - 1, season.endDay);

  // Season spans a year boundary (e.g. Nov 1 → Feb 28)
  if (end < start) {
    // In Season: today is after start OR on/before end
    if (today >= start || today <= end) return 'in-season';

    // Upcoming: within threshold days before this year's start
    const upcomingFrom = new Date(start);
    upcomingFrom.setDate(upcomingFrom.getDate() - thresholdDays);
    if (today >= upcomingFrom) return 'upcoming';

    // Upcoming: within threshold days before next year's start
    const startNext = new Date(y + 1, season.startMonth - 1, season.startDay);
    const upcomingFromNext = new Date(startNext);
    upcomingFromNext.setDate(upcomingFromNext.getDate() - thresholdDays);
    if (today >= upcomingFromNext && today < startNext) return 'upcoming';

    return 'out-of-season';
  }

  // Normal season (start and end in the same calendar year)
  if (today >= start && today <= end) return 'in-season';

  const upcomingFrom = new Date(start);
  upcomingFrom.setDate(upcomingFrom.getDate() - thresholdDays);
  if (today >= upcomingFrom && today < start) return 'upcoming';

  return 'out-of-season';
}
